import { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import TournamentSelector from '../components/TournamentSelector';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Upload, FileSpreadsheet, AlertCircle, Trash2, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BudgetUpload = () => {
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [fiscalYear, setFiscalYear] = useState('');
  const [clearFiscalYear, setClearFiscalYear] = useState('all');
  const [replaceExisting, setReplaceExisting] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'text/csv',
      ];
      if (!validTypes.includes(selectedFile.type)) {
        toast.error('Please upload a valid Excel (.xlsx, .xls) or CSV file');
        return;
      }
      setFile(selectedFile);
    }
  };

  const downloadTemplate = () => {
    const header = ['budget_head', 'allocated_amount', 'department', 'notes'];
    const sampleRows = [
      ['Marketing', '50000', 'Sales', 'Digital + offline campaigns'],
      ['IT Infrastructure', '75000', 'Technology', 'Servers & licenses'],
      ['Team Travel', '120000', 'Operations', ''],
    ];
    const escape = (v) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [header, ...sampleRows]
      .map((row) => row.map(escape).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const suffix = fiscalYear ? `_${fiscalYear.replace(/\s+/g, '_')}` : '';
    const link = document.createElement('a');
    link.href = url;
    link.download = `budget_template${suffix}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Template downloaded — fill and upload above');
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!file) {
      toast.error('Please select a file');
      return;
    }

    if (!fiscalYear) {
      toast.error('Please enter fiscal year');
      return;
    }

    // Iter 37 guardrail: replace-mode wipes ALL existing budgets for that FY
    // — regardless of who originally uploaded them. The previous behaviour
    // silently scoped the delete to the current user's own rows and the
    // subsequent insert duplicated everything. We've fixed the server side;
    // now demand explicit confirmation on the client too so an admin who
    // double-clicks Upload doesn't silently torch a colleague's work.
    if (replaceExisting) {
      const existingCount = await axios
        .get(`${API}/budgets?fiscal_year=${encodeURIComponent(fiscalYear)}`, { withCredentials: true })
        .then((r) => (Array.isArray(r.data) ? r.data.length : 0))
        .catch(() => null);
      const msg =
        existingCount === null
          ? `You're about to REPLACE all existing budgets for ${fiscalYear}. ` +
            `Any rows from other admins will be removed. Continue?`
          : `Replace mode will DELETE all ${existingCount} existing budgets ` +
            `for ${fiscalYear} (regardless of who uploaded them) and import ` +
            `from "${file.name}". Continue?`;
      if (!window.confirm(msg)) {
        return;
      }
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/budgets/upload?fiscal_year=${fiscalYear}&replace_existing=${replaceExisting}`,
        formData,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      toast.success(response.data.message);
      setFile(null);
      setFiscalYear('');
      document.getElementById('file-upload').value = '';
    } catch (error) {
      console.error('Upload error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Upload failed';
      toast.error(`Upload failed: ${errorMsg}`);
    } finally {
      setUploading(false);
    }
  };

  const handleClearBudgets = async () => {
    setClearing(true);
    try {
      const url = clearFiscalYear && clearFiscalYear !== 'all'
        ? `${API}/budgets/clear?fiscal_year=${clearFiscalYear}`
        : `${API}/budgets/clear`;
      
      const response = await axios.delete(url, { withCredentials: true });
      
      toast.success(response.data.message);
      setClearFiscalYear('all');
      setClearDialogOpen(false);
    } catch (error) {
      console.error('Clear error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to clear budgets';
      toast.error(`Clear failed: ${errorMsg}`);
    } finally {
      setClearing(false);
    }
  };

  // Check if user has permission to upload budgets
  const canUploadBudget = user?.is_super_admin || user?.tab_permissions?.budget_upload;
  
  if (!canUploadBudget) {
    return (
      <div data-testid="access-denied" className="flex items-center justify-center h-96">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="font-heading">Access Denied</CardTitle>
            <CardDescription>You don't have permission to upload budgets</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="budget-upload-page" className="space-y-8 max-w-4xl">
      <div>
        <h1 className="text-4xl font-heading font-bold tracking-tight">
          Budget Upload
        </h1>
        <p className="text-muted-foreground mt-1">
          Upload budget data from Excel or CSV files
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Upload Form */}
        <Card data-testid="upload-form-card" className="border-border">
          <CardHeader className="border-b border-border/50">
            <CardTitle className="font-heading">Upload Budget File</CardTitle>
            <CardDescription>Excel (.xlsx, .xls) or CSV format</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleUpload} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="fiscal-year">Tournament</Label>
                <TournamentSelector
                  value={fiscalYear}
                  onChange={setFiscalYear}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  data-testid="replace-existing-checkbox"
                  id="replace-existing"
                  checked={replaceExisting}
                  onCheckedChange={setReplaceExisting}
                />
                <Label
                  htmlFor="replace-existing"
                  className="text-sm font-normal cursor-pointer"
                >
                  Replace existing budgets for this fiscal year
                </Label>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="file-upload">Budget File</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={downloadTemplate}
                    data-testid="download-template-button"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Template
                  </Button>
                </div>
                <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary transition-colors">
                  <Input
                    data-testid="file-input"
                    id="file-upload"
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <label
                    htmlFor="file-upload"
                    className="cursor-pointer flex flex-col items-center gap-2"
                  >
                    <FileSpreadsheet className="h-12 w-12 text-muted-foreground" strokeWidth={1.5} />
                    <div>
                      <p className="text-sm font-medium">
                        {file ? file.name : 'Click to upload or drag and drop'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Excel or CSV files only · use the template above for best results
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              <Button
                data-testid="upload-button"
                type="submit"
                className="w-full"
                disabled={uploading || !file}
              >
                <Upload className="h-4 w-4 mr-2" />
                {uploading ? 'Uploading...' : 'Upload Budget'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Clear Budget Data Card */}
        <Card data-testid="clear-budget-card" className="border-border border-destructive/30">
          <CardHeader className="border-b border-border/50">
            <CardTitle className="font-heading text-destructive">Clear Budget Data</CardTitle>
            <CardDescription>Remove budget entries from the system</CardDescription>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="clear-fiscal-year">Tournament (Optional)</Label>
              <TournamentSelector
                value={clearFiscalYear}
                onChange={setClearFiscalYear}
                showAllOption={true}
              />
              <p className="text-xs text-muted-foreground">
                Leave empty to clear all budget data, or select a specific tournament
              </p>
            </div>

            <AlertDialog open={clearDialogOpen} onOpenChange={setClearDialogOpen}>
              <AlertDialogTrigger asChild>
                <Button
                  data-testid="clear-budget-button"
                  variant="destructive"
                  className="w-full"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Clear Budget Data
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete{' '}
                    {clearFiscalYear && clearFiscalYear !== 'all' ? (
                      <span className="font-semibold">
                        all budget entries for fiscal year {clearFiscalYear}
                      </span>
                    ) : (
                      <span className="font-semibold">all budget entries across all fiscal years</span>
                    )}
                    .
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="cancel-clear-button">Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    data-testid="confirm-clear-button"
                    onClick={handleClearBudgets}
                    disabled={clearing}
                    className="bg-destructive hover:bg-destructive/90"
                  >
                    {clearing ? 'Clearing...' : 'Yes, Clear Data'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <div className="p-4 bg-destructive/10 rounded-lg border border-destructive/30">
              <div className="flex gap-2">
                <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0" strokeWidth={1.5} />
                <div className="text-sm text-muted-foreground">
                  <p className="font-medium text-foreground mb-1">Warning:</p>
                  <p>
                    Clearing budget data will remove all budget entries you've uploaded.
                    This operation is permanent and cannot be undone.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card data-testid="instructions-card" className="border-border">
          <CardHeader className="border-b border-border/50">
            <CardTitle className="font-heading">File Format Requirements</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <div className="space-y-2">
              <h3 className="font-semibold text-sm">Required Columns:</h3>
              <ul className="text-sm space-y-1 list-disc list-inside text-muted-foreground">
                <li>
                  <code className="bg-muted px-1 py-0.5 rounded text-xs">budget_head</code> -
                  Name of the budget category
                </li>
                <li>
                  <code className="bg-muted px-1 py-0.5 rounded text-xs">
                    allocated_amount
                  </code>{' '}
                  - Budgeted amount (number)
                </li>
              </ul>
            </div>

            <div className="space-y-2">
              <h3 className="font-semibold text-sm">Optional Columns:</h3>
              <ul className="text-sm space-y-1 list-disc list-inside text-muted-foreground">
                <li>
                  <code className="bg-muted px-1 py-0.5 rounded text-xs">department</code> -
                  Department name
                </li>
                <li>
                  <code className="bg-muted px-1 py-0.5 rounded text-xs">notes</code> -
                  Additional notes
                </li>
              </ul>
            </div>

            <div className="mt-4 p-4 bg-muted/30 rounded-lg border border-border">
              <div className="flex gap-2">
                <AlertCircle className="h-5 w-5 text-info flex-shrink-0" strokeWidth={1.5} />
                <div className="text-sm text-muted-foreground">
                  <p className="font-medium text-foreground mb-1">Example:</p>
                  <div className="font-mono text-xs bg-white p-2 rounded border">
                    budget_head, allocated_amount, department
                    <br />
                    Marketing, 50000, Sales
                    <br />
                    IT Infrastructure, 75000, Technology
                  </div>
                </div>
              </div>
            </div>

            <Button
              type="button"
              variant="secondary"
              className="w-full"
              onClick={downloadTemplate}
              data-testid="download-template-instructions-button"
            >
              <Download className="h-4 w-4 mr-2" />
              Download Budget Template (CSV)
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BudgetUpload;
